/**
 * Typed error thrown by the API client for any non-2xx response. Wraps the
 * server error envelope plus the HTTP status so UI code can branch on either
 * the stable `code` (preferred) or the `httpStatus` for transport-level cases.
 */
export class AuthApiError extends Error {
    code;
    httpStatus;
    constructor(envelope, httpStatus) {
        super(envelope.message);
        this.name = 'AuthApiError';
        this.code = envelope.errorCode;
        this.httpStatus = httpStatus;
    }
    /**
     * Returns true if the error is one of the well-known field-level codes the
     * UI typically wants to surface near a specific input rather than as a banner.
     */
    isFieldError() {
        return (this.code === 'AUTH_INVALID_CREDENTIALS' ||
            this.code === 'AUTH_IDENTITY_ALREADY_EXISTS' ||
            this.code === 'AUTH_WEAK_PASSWORD' ||
            this.code === 'AUTH_INVALID_CODE' ||
            this.code === 'AUTH_IDENTITY_NOT_FOUND' ||
            this.code === 'AUTH_INVALID_GROUP');
    }
}
/**
 * Catch-all UI string lookup keyed by `errorCode`. Covers the codes documented
 * in the API contract; anything missing falls back to the server-supplied
 * message. Host apps can spread this with their own overrides.
 *
 * ## The keys carry the `AUTH_` prefix because the server sends it
 *
 * Every key below was written without it, and **not one of them ever matched**. The auth errors
 * are constructed in `packages/auth-middleware/src/core/errors.ts` and each one names itself
 * `AUTH_…` — `AUTH_INVALID_CODE`, `AUTH_FLOW_EXPIRED`, `AUTH_WEAK_PASSWORD` — so
 * {@link resolveErrorMessage} fell through this map on every auth error the platform can raise
 * and rendered the **raw server sentence** instead, and {@link AuthApiError.isFieldError}
 * returned `false` for every error it exists to recognise. No host app passes an override map,
 * so this table was unreachable in its entirety.
 *
 * That is what `USR-004` AC-2 — *"a used or expired link explains itself"* — was failing on: the
 * explanation was written, and the lookup that would have shown it could not find it.
 *
 * The three unprefixed keys that stay unprefixed are the three that are genuinely unprefixed:
 * `PERMISSION_DENIED`, `AUTHENTICATION_REQUIRED` and `ACCOUNT_SUSPENDED` come from
 * `@h2hspark/errors`, not from the auth package, and their codes carry no prefix.
 *
 * Codes deliberately absent: `AUTH_FLOW_ERROR` is a catch-all whose message is the useful part,
 * and `AUTH_SIGN_IN_METHOD_NOT_OFFERED` is a per-surface refusal whose server sentence names the
 * surface. Both are better read from the server than replaced by a generic line here.
 */
export const defaultErrorMessages = {
    AUTH_INVALID_CREDENTIALS: 'Email or password is incorrect.',
    AUTH_IDENTITY_ALREADY_EXISTS: 'An account with that email already exists.',
    AUTH_WEAK_PASSWORD: 'That password is too weak. Use at least 8 characters, including a number and a symbol.',
    // `USR-004` AC-2, the expired half. The only way on is a fresh send, and the form's own
    // "start over" control is it — so the sentence points at that and promises nothing else.
    AUTH_FLOW_EXPIRED: 'That code has expired. Request a new one to continue.',
    // `USR-004` AC-2, the used half — **and it has to cover the mistyped half in the same
    // sentence**, because Cognito does not separate them: a code that has already been spent comes
    // back as `CodeMismatchException`, exactly like a typo, so there is no evidence here for a
    // message that claims to know which happened. Saying "already used" to somebody who fat-
    // fingered a digit is a worse failure than saying both.
    AUTH_INVALID_CODE: 'That code is not correct, or it has already been used. Request a new one.',
    AUTH_IDENTITY_NOT_FOUND: 'No account matches that email.',
    AUTH_MFA_REQUIRED: 'Additional verification is required to finish signing in.',
    AUTH_INVALID_GROUP: 'One of the selected groups is not allowed for this app.',
    PERMISSION_DENIED: 'You do not have permission to perform that action.',
    AUTHENTICATION_REQUIRED: 'Sign in to continue.',
    // `PLT-014` AC-7. Reached at the sign-in form itself when the pool admits a credential whose
    // roster row is suspended — the session gate refuses the login with this code — so the form
    // says what happened rather than "email or password is incorrect".
    ACCOUNT_SUSPENDED: 'This account has been suspended. Contact H2HSpark to have it reinstated.',
};
/**
 * Resolves a user-facing message for an error. Prefers the lookup override,
 * then `defaultErrorMessages`, then the server message; falls back to a generic
 * sentence when nothing else is available.
 */
export function resolveErrorMessage(error, overrides = {}) {
    if (error instanceof AuthApiError) {
        const mapped = overrides[error.code] ?? defaultErrorMessages[error.code];
        if (mapped !== undefined)
            return mapped;
        if (error.code === VALIDATION_FAILED)
            return plainValidationMessage(error.message);
        return error.message;
    }
    if (error instanceof Error && error.message.length > 0)
        return error.message;
    return 'Something went wrong. Please try again.';
}
/** The code `@h2hspark/errors`' `ValidationError` carries. Unprefixed, like `PERMISSION_DENIED`. */
const VALIDATION_FAILED = 'VALIDATION_FAILED';
/** What a refusal says when none of its reasons is a sentence somebody can act on. */
export const VALIDATION_FALLBACK_MESSAGE = 'Some of what you entered could not be accepted. Check it and try again.';
/**
 * A request-body refusal, in the words of its reasons and nothing else (TW 44131867).
 *
 * The auth routes refuse a body as `Request body invalid: <field>: <reason>; <field>: <reason>` —
 * a line written for whoever reads the API's logs. The set-new-password screen printed it whole:
 * *"Request body invalid: newPassword: Password must contain a symbol."* The reasons the platform
 * writes itself are sentences (`describePasswordViolation`'s *"Password must contain a symbol."*),
 * so those are kept and the prefix and the field paths go. A reason that is not a sentence —
 * zod's own *"Invalid email"* — is a developer's wording too, and is replaced by one line that
 * asks the person to check what they typed.
 */
export function plainValidationMessage(message) {
    const withoutPrefix = message.replace(/^[^:]*\binvalid:\s*/iu, '');
    const reasons = withoutPrefix
        .split(/;\s*/u)
        .map((issue) => issue.replace(/^[\w.<>[\]-]+:\s*/u, '').trim())
        .filter((reason) => /^[A-Z].*[.!?]$/u.test(reason));
    const unique = [...new Set(reasons)];
    return unique.length > 0 ? unique.join(' ') : VALIDATION_FALLBACK_MESSAGE;
}
//# sourceMappingURL=errors.js.map